import {
  ProposedFeatures,
  BrowserMessageReader,
  BrowserMessageWriter,
  createConnection,
  TextDocuments,
} from 'vscode-languageserver/browser';
import { TextDocument } from 'vscode-languageserver-textdocument';
import { Container, brandInstance } from '@gitlab-org/di';
import {
  ConfigService,
  DefaultConfigService,
  DefaultDocumentTransformerService,
  DefaultStreamingHandler,
  GitLabAPI,
  GitLabApiClient,
  setup,
} from '../common';
import { DefaultSecretRedactor } from '../common/secret_redaction';
import { log } from '../common/log';
import { LsConnection, LsTextDocuments } from '../common/external_interfaces';
import { LsFetch } from '../common/fetch';
import { DefaultFeatureFlagService, FeatureFlagService } from '../common/feature_flags';
import { SnowplowTracker } from '../common/tracking/snowplow_tracker';
import { getLanguageServerVersion } from '../common/utils/get_language_server_version';
import { InstanceTracker } from '../common/tracking/instance_tracker';
import { MultiTracker } from '../common/tracking/multi_tracker';
import { DefaultAdvancedContextService } from '../common/advanced_context/advanced_context_service';
import { DefaultDocumentService, DocumentService } from '../common/document_service';
import { DefaultTokenCheckNotifier } from '../common/core/handlers/token_check_notifier';
import { DefaultSecurityDiagnosticsPublisher } from '../common/security_diagnostics_publisher';
import { DefaultConnectionService } from '../common/connection_service';
import { DefaultInitializeHandler } from '../common/core/handlers/initialize_handler';
import { TelemetryTracker } from '../common/tracking/tracking_types';
import { DefaultSupportedLanguagesService } from '../common/suggestion/supported_languages_service';
import {
  DefaultFeatureStateManager,
  DefaultCodeSuggestionsSupportedLanguageCheck,
  DefaultProjectDuoAccessCheck,
  DefaultCodeSuggestionsDuoLicenseCheck,
  DefaultCodeSuggestionsInstanceVersionCheck,
} from '../common/feature_state';
import { DefaultDuoProjectAccessChecker } from '../common/services/duo_access/project_access_checker';
import {
  DefaultDuoProjectAccessCache,
  DuoProjectAccessCache,
} from '../common/services/duo_access/project_access_cache';
import { DeafultPostProcessorPipeline } from '../common/suggestion_client/post_processors/default_post_processor_pipeline';
import { DefaultSuggestionApiErrorCheck } from '../common/feature_state/suggestion_api_error_check';
import { EmptyFileResolver, DefaultDirectoryWalker } from '../common/services/fs';
import { DefaultSuggestionApiErrorNotifier } from '../common/feature_state/suggestion_api_error_notifier';
import { DefaultSuggestionService } from '../common/suggestion/suggestion_service';
import { NoopSentryTracker } from '../common/tracking/error_tracker';
import { DefaultErrorHandler } from '../common/errors/error_handler';
import {
  DefaultVirtualFileSystemService,
  VirtualFileSystemService,
} from '../common/services/fs/virtual_file_system_service';
import { DefaultRepositoryService } from '../common/services/git/repository_service';
import { EmptyFsClient } from '../common/services/fs/fs';
import { BrowserTreeSitterParser } from './tree_sitter';
import { Fetch } from './fetch';

async function main() {
  // eslint-disable-next-line no-restricted-globals
  const worker: Worker = self as unknown as Worker;
  const messageReader = new BrowserMessageReader(worker);
  const messageWriter = new BrowserMessageWriter(worker);

  const container = new Container();
  container.instantiate(DefaultConfigService, DefaultSupportedLanguagesService);
  const lsFetch = new Fetch();
  container.addInstances(brandInstance(LsFetch, lsFetch));
  const connection = createConnection(ProposedFeatures.all, messageReader, messageWriter);
  container.addInstances(brandInstance(LsConnection, connection));
  const documents: TextDocuments<TextDocument> = new TextDocuments(TextDocument);
  container.addInstances(brandInstance(LsTextDocuments, documents));

  const configService = container.get(ConfigService);

  const documentService = new DefaultDocumentService(documents);
  container.addInstances(brandInstance(DocumentService, documentService));

  container.instantiate(
    GitLabAPI,
    DefaultDocumentTransformerService,
    DefaultFeatureFlagService,
    DefaultTokenCheckNotifier,
    DefaultSecretRedactor,
    DefaultSecurityDiagnosticsPublisher,
    DefaultInitializeHandler,
    DefaultCodeSuggestionsSupportedLanguageCheck,
    DefaultProjectDuoAccessCheck,
    DefaultFeatureStateManager,
    DefaultAdvancedContextService,
    DefaultDuoProjectAccessChecker,
    DefaultDuoProjectAccessCache,
    DefaultCodeSuggestionsDuoLicenseCheck,
    DefaultCodeSuggestionsInstanceVersionCheck,
    DefaultDirectoryWalker,
    EmptyFileResolver,
    BrowserTreeSitterParser,
    DeafultPostProcessorPipeline,
    DefaultSuggestionApiErrorCheck,
    DefaultSuggestionApiErrorNotifier,
    NoopSentryTracker,
    DefaultErrorHandler,
    DefaultVirtualFileSystemService,
    DefaultRepositoryService,
    EmptyFsClient,
  );
  const snowplowTracker = new SnowplowTracker(
    lsFetch,
    container.get(ConfigService),
    container.get(FeatureFlagService),
    container.get(GitLabApiClient),
  );
  const instanceTracker = new InstanceTracker(
    container.get(GitLabApiClient),
    container.get(ConfigService),
  );
  const telemetryTracker = new MultiTracker([snowplowTracker, instanceTracker]);

  container.addInstances(brandInstance(TelemetryTracker, telemetryTracker));

  // FIXME: move the Telemetry tracker construction to DI so we don't have
  // order-dependent initialization like with stream handler
  container.instantiate(
    DefaultStreamingHandler,
    DefaultConnectionService,
    DefaultSuggestionService,
  );

  log.setup(configService);

  const version = getLanguageServerVersion();
  log.info(`GitLab Language Server is starting (v${version})`);

  await lsFetch.initialize();

  setup(
    telemetryTracker,
    connection,
    container.get(FeatureFlagService),
    configService,
    undefined,
    undefined,
    container.get(DuoProjectAccessCache),
    container.get(VirtualFileSystemService),
  );

  // Make the text document manager listen on the connection for open, change and close text document events
  documents.listen(connection);
  // Listen on the connection
  connection.listen();

  log.info('GitLab Language Server has started');
}

main().catch((e) => log.error(e));
